/**
 * Experiment Manager for B2M Tracker
 *
 * Owns the lifecycle of A/B test assignments on the client:
 *   - Persists assignments in localStorage so a returning user keeps
 *     the same group across tabs and browser restarts.
 *   - Refreshes from the backend when an assignment is missing or stale,
 *     so winner-group changes propagate within the TTL window.
 *   - Emits a single ab_test_view event per (session, experiment) so the
 *     server can attribute downstream behaviour to the assigned group.
 */
const CACHE_KEY = 'b2m_experiment_assignments';
const SESSION_EXPOSURE_KEY = 'b2m_experiment_exposed_in_session';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export default class ExperimentManager {
  constructor(tracker) {
    this.tracker = tracker;
  }

  get ttl() {
    return (this.tracker.config && this.tracker.config.experimentCacheTtl) || DEFAULT_TTL_MS;
  }

  _readCache() {
    return this.tracker.storage.get(CACHE_KEY, {}) || {};
  }

  _writeCache(cache) {
    return this.tracker.storage.set(CACHE_KEY, cache);
  }

  _isFresh(entry) {
    if (!entry || !entry.assignedAt) return false;
    return (Date.now() - entry.assignedAt) <= this.ttl;
  }

  _findMissing(experimentIds, cache) {
    return experimentIds.filter(id => !this._isFresh(cache[id]));
  }

  /**
   * Resolve group assignments for the requested experiments.
   *
   * @param {string[]} experimentIds
   * @param {Object} [properties={}] - targeting attributes (country, device, …)
   * @param {Object} [options]
   * @param {boolean} [options.forceRefresh=false] - bypass cache and re-fetch all
   * @returns {Promise<Array<{ExperimentId, GroupId, GroupName}>>}
   */
  async assign(experimentIds, properties = {}, options = {}) {
    if (!Array.isArray(experimentIds) || experimentIds.length === 0) {
      this.tracker.logger.warn('[B2M Experiments] assign called without experiment IDs');
      return [];
    }

    const userId = this.tracker.userId.getId();
    if (!userId) {
      this.tracker.logger.warn('[B2M Experiments] assign skipped — user ID not ready');
      return [];
    }

    const { forceRefresh = false } = options;
    const cache = this._readCache();
    const missing = forceRefresh ? experimentIds.slice() : this._findMissing(experimentIds, cache);

    if (missing.length > 0) {
      try {
        const fresh = await this.tracker.experimentApi.assign(userId, missing, properties);
        const now = Date.now();
        fresh.forEach(item => {
          if (!item || !item.ExperimentId) return;
          cache[item.ExperimentId] = {
            groupId: item.GroupId,
            groupName: item.GroupName,
            assignedAt: now,
            userId
          };
        });
        this._writeCache(cache);
        this.tracker.logger.log('[B2M Experiments] Assignments refreshed:', fresh);
      } catch (e) {
        // Fail-soft: keep serving whatever cached assignments we already have
        this.tracker.logger.error('[B2M Experiments] assign failed, falling back to cache:', e);
      }
    } else {
      this.tracker.logger.log('[B2M Experiments] All assignments served from cache');
    }

    const result = experimentIds
      .map(id => {
        const entry = cache[id];
        if (!entry) return null;
        return {
          ExperimentId: id,
          GroupId: entry.groupId,
          GroupName: entry.groupName
        };
      })
      .filter(Boolean);

    this._trackExposure(result);
    return result;
  }

  /**
   * Synchronous lookup — returns the cached group name for an experiment
   * or null if the user has not been assigned yet.
   */
  getGroup(experimentId) {
    const cache = this._readCache();
    const entry = cache[experimentId];
    return entry ? entry.groupName : null;
  }

  /**
   * Returns the full cached assignment object including group ID.
   */
  getAssignment(experimentId) {
    const cache = this._readCache();
    const entry = cache[experimentId];
    if (!entry) return null;
    return {
      ExperimentId: experimentId,
      GroupId: entry.groupId,
      GroupName: entry.groupName
    };
  }

  /**
   * Wipe cached assignments. Call this when the user logs out or
   * their identity changes so the next user does not inherit groups.
   */
  clear() {
    this.tracker.storage.remove(CACHE_KEY);
    this.tracker.storage._sessionRemove(SESSION_EXPOSURE_KEY);
  }

  /**
   * Emit ab_test_view exactly once per (browser session, experiment)
   * so backend attribution stays clean while still capturing every
   * unique exposure.
   */
  _trackExposure(assignments) {
    if (!assignments || assignments.length === 0) return;

    const exposed = this.tracker.storage._sessionGet(SESSION_EXPOSURE_KEY) || {};
    let updated = false;

    assignments.forEach(({ ExperimentId, GroupId, GroupName }) => {
      if (exposed[ExperimentId]) return;

      try {
        if (window.b2m && typeof window.b2m.track === 'function') {
          window.b2m.track('ab_test_view', {
            experiment_id: ExperimentId,
            group_id: GroupId,
            group_name: GroupName
          });
        }
        exposed[ExperimentId] = true;
        updated = true;
      } catch (e) {
        this.tracker.logger.error('[B2M Experiments] exposure tracking failed:', e);
      }
    });

    if (updated) {
      this.tracker.storage._sessionSet(SESSION_EXPOSURE_KEY, exposed);
    }
  }
}
